// Theme Configuration - Matching Customer Mobile App
export const theme = {
  // Colors - Matching Mobile App Theme
  colors: {
    // Primary: Blue - Main brand color (used for logo, headings, CTA buttons)
    primary: '#2563eb',        // Blue - primary brand color
    primaryDark: '#1e40af',   // Darker blue for hover states
    primaryLight: '#3b82f6',   // Lighter blue
    
    // Secondary: Orange - Primary action color (used for buttons, CTAs, active states)
    secondary: '#FF6B35',      // Vibrant orange - used for "Get Started", "Home" button, main heading
    secondaryDark: '#E55A2B',  // Darker orange
    secondaryLight: '#FF8C5A', // Lighter orange
    
    // Brand Blue: For logo and brand elements
    brandBlue: '#2563eb',      // Brand blue (logo color)
    brandBlueDark: '#1e40af',  // Darker blue
    brandBlueLight: '#3b82f6', // Lighter blue
    
    // Accent: Warm Gold/Amber (for special accents)
    accentGold: '#F9C74F',     // Warm Amber
    accentGoldDark: '#E8B43C', // Darker amber
    accentGoldLight: '#FAD66F', // Lighter amber
    
    // Accent: Success Green
    accent: '#2D8659',         // Success Green
    accentDark: '#256B48',     // Darker green
    accentLight: '#3FA370',    // Lighter green
    
    // Background & Surface - Pure white matching website
    background: '#FFFFFF',      // Pure white background
    surface: '#FFFFFF',         // White surface
    card: '#FFFFFF',            // White card background
    
    // Text Colors - Black and grays
    text: '#000000',            // Black for primary text
    textSecondary: '#6b7280',   // Medium gray for secondary text
    textLight: '#9ca3af',       // Light gray text
    textMuted: '#6b7280',      // Muted text
    
    // UI Colors
    border: '#e5e7eb',          // Light gray border
    input: '#f9fafb',           // Very light gray input background
    error: '#ef4444',           // Red - errors
    success: '#10b981',         // Green - success
    warning: '#f59e0b',         // Amber - warnings
    info: '#3b82f6',            // Blue - info
    
    // Muted
    muted: '#f5f5f5',           // Muted background
    mutedForeground: '#6b7280', // Muted text
  },

  // Typography - Matching Mobile App
  typography: {
    // Font Sizes
    h1: 40,           // Main heading
    h2: 32,           // Section headings
    h3: 24,           // Subsection headings
    h4: 20,           // Card titles
    body: 16,         // Body text, navigation links, descriptions
    bodySmall: 14,    // Service labels, taglines
    caption: 12,      // Small captions
    button: 16,       // Button text
    
    // Font Weights
    light: '300',
    regular: '400',
    medium: '500',
    semiBold: '600',
    bold: '700',
    
    // Line Heights
    lineHeight: {
      tight: 1.2,      // h1
      normal: 1.3,     // h2, h3, h4
      body: 1.7,       // body text
      relaxed: 1.6,    // body-sm
    },
    
    // Letter Spacing
    letterSpacing: {
      tight: -0.02,    // h1, h2, h3
      normal: -0.01,   // body
    },
  },

  // Spacing
  spacing: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
  },

  // Border Radius
  borderRadius: {
    sm: 8,      // Small elements
    md: 12,     // Buttons, cards
    lg: 16,     // Larger cards, rounded buttons
    xl: 20,     // Extra large rounded
    '2xl': 24,  // Very large rounded
    full: 9999, // Full circle
  },

  // Shadows - Very subtle, soft shadows
  shadows: {
    soft: '0 1px 4px rgba(0, 0, 0, 0.04)',
    card: '0 1px 4px rgba(0, 0, 0, 0.04)',
    button: '0 1px 3px rgba(0, 0, 0, 0.05)',
    medium: '0 2px 8px rgba(0, 0, 0, 0.06)',
    elevated: '0 4px 12px rgba(0, 0, 0, 0.08)',
  },
  
  // Gradients
  gradients: {
    primary: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
    secondary: 'linear-gradient(135deg, #FF6B35 0%, #FF8C5A 100%)',
    accent: 'linear-gradient(135deg, #2563eb 0%, #FF6B35 100%)',
    hero: 'linear-gradient(135deg, rgba(37, 99, 235, 0.08) 0%, rgba(255, 107, 53, 0.08) 100%)',
    gold: 'linear-gradient(135deg, #F4A261 0%, #F9C74F 100%)',
  },
};

export type Theme = typeof theme;

// Export theme utilities
export const getThemeColor = (colorKey: keyof typeof theme.colors) => theme.colors[colorKey];
export const getThemeSpacing = (size: keyof typeof theme.spacing) => `${theme.spacing[size]}px`;
export const getThemeRadius = (size: keyof typeof theme.borderRadius) => `${theme.borderRadius[size]}px`;
export const getThemeShadow = (type: keyof typeof theme.shadows) => theme.shadows[type];
export const getThemeGradient = (type: keyof typeof theme.gradients) => theme.gradients[type];

